import { useEffect, useState } from "react";

export interface FlowboxScriptProps {
  flowKey: string;
  productId?: string | number;
  tags?: string[];
  locale: string;
  enableTimeout?: boolean;
  islazyLoad?: boolean;
}

const useFlowboxScript = ({
  flowKey,
  productId = "",
  tags = [],
  locale,
  enableTimeout,
  islazyLoad = true,
}: FlowboxScriptProps) => {
  const [containerId, setContainerId] = useState<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);

  useEffect(() => {
    if (isContentReady) return;

    const id = `js-flowbox-flow-${flowKey}`;
    setContainerId(id);

    const loadFlowboxScript = async (): Promise<void> => {
      if (isScriptLoaded) {
        return;
      }

      return await new Promise<void>((resolve) => {
        if (!window.flowbox) {
          const flowboxFunction = function (...args: unknown[]) {
            flowboxFunction.q.push(args);
          };
          flowboxFunction.q = [];
          window.flowbox = flowboxFunction;
        }

        const scriptId = "flowbox-js-embed";
        const existingScript = document.getElementById(scriptId);
        if (existingScript) {
          setIsScriptLoaded(true);
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.id = scriptId;
        script.async = true;
        script.src = "https://connect.getflowbox.com/flowbox.js";
        script.onload = () => {
          setIsScriptLoaded(true);
          resolve();
        };
        document.body.appendChild(script);
      });
    };

    loadFlowboxScript()
      .then(() => {
        if (window.flowbox && id) {
          window.flowbox("init", {
            container: `#${id}`,
            key: flowKey,
            locale,
            productId,
            tags,
            lazyLoad: islazyLoad,
          });
        }
      })
      .catch((error) => {
        console.error("Failed to load Flowbox script:", error);
      });

    // Re-initialize Flowbox if the script is already loaded
    if (isScriptLoaded && window.flowbox && id) {
      window.flowbox("init", {
        container: `#${id}`,
        key: flowKey,
        locale,
        productId,
        tags,
      });
    }
  }, [flowKey, productId, tags, isScriptLoaded, locale, isContentReady]);

  // Check if the content is ready before isScriptLoaded
  useEffect(() => {
    if (isContentReady) return;
    setIsContentReady(false);

    const timeoutId = enableTimeout
      ? setTimeout(() => {
          setIsContentReady(true);
        }, 4500)
      : null;

    const container = document.getElementById(containerId!);

    // Check if the container has children
    if (container) {
      if (container.children.length > 0) {
        setIsContentReady(true);
        enableTimeout && timeoutId && clearTimeout(timeoutId);
      } else {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === "childList") {
              if (container.children.length > 0) {
                setIsContentReady(true); // if the container has children, set to `true`
                observer.disconnect();
                enableTimeout && timeoutId && clearTimeout(timeoutId); // clear the timer
              }
            }
          });
        });

        observer.observe(container, { childList: true, subtree: true });
      }
    }

    return () => {
      enableTimeout && timeoutId && clearTimeout(timeoutId);
    };
  }, [isScriptLoaded, containerId, productId, isContentReady]);
  return { containerId, isScriptLoaded, isContentReady };
};

export default useFlowboxScript;
